import { i18n } from '@/i18n';
import { DashboardInformation, DimensionType, DimensionTypes, MetricType, MetricTypes } from '../domains/entities/Dashboard';
import { DashboardService } from '../domains/services/DashboardService';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { FILTER_OPTIONS } from '../constants';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';

/**
 * 在庫ダッシュボードページのViewModel
 */
export class InventoryDashboardViewModel {
  private t = i18n.t;

  public readonly dimensionTypesMapper = {
    [DimensionTypes.AggregateBusinessResponsibleUser]: '仕入担当者',
    [DimensionTypes.BaseBusinessResponsibleUser]: '販売担当者',
    [DimensionTypes.Warehouse]: '置場',
    [DimensionTypes.Supplier]: '仕入先',
    [DimensionTypes.Business]: '商品',
    [DimensionTypes.Package]: '容量',
    [DimensionTypes.Customer]: '売上先',
    [DimensionTypes.SalesDestination]: '送り先',
    [DimensionTypes.Manufacturer]: '製造社',
    [DimensionTypes.EndUser]: 'エンドユーザー',
    [DimensionTypes.OrderPackage]: '発注単位',
    [DimensionTypes.Moq]: 'MOQ',
    [DimensionTypes.Unit]: '単位',
    [DimensionTypes.Group]: 'グループ',
    [DimensionTypes.LeadTime]: 'リードタイム',
    [DimensionTypes.StdInventoryIndexMin]: '標準在庫指数(min)',
    [DimensionTypes.StdInventoryIndexMax]: '標準在庫指数(max)',
    [DimensionTypes.UserSpecificNumber]: 'ユーザー固有番号',
  } as const satisfies Record<Exclude<DimensionType, 'DIMENSION_UNKNOWN'>, string>;

  public readonly metricTypesMapper = {
    [MetricTypes.Turnover]: '在庫回転率',
    [MetricTypes.Inventory]: '在庫数量',
    [MetricTypes.SalesResult]: '販売実績数量',
    [MetricTypes.Arrival]: '入荷数量',
    [MetricTypes.OrderAmount]: '発注数量',
    [MetricTypes.CustomerForecast]: '客先内示',
    [MetricTypes.SupplierForecast]: '仕入先内示',
    [MetricTypes.ForecastAccuracy]: '内示正答率',
  } as const satisfies Record<Exclude<MetricType, 'METRIC_UNKNOWN'>, string>;

  public readonly numberHeaderKeys = [
    'package',
    'salesResult',
    'endInventory',
    'turnover',
    'customerForecast',
    'arrival',
    'forecastAccuracy',
    'supplierForecast',
    'orderAmount',
  ];

  public readonly forecastAccuracyToNumber = {
    前月の正答率: 1,
    直近3ヶ月平均: 3,
    直近6ヶ月平均: 6,
    直近12ヶ月平均: 12,
  } satisfies Record<(typeof FILTER_OPTIONS.ANNOUNCE_ACCURACY)[number], number>;

  /**
   * DimensionTypesを表示用の値にする
   * @returns - DimensionTypesを表示用の値にして返す
   */
  public getDimensionTypesDisplayValue() {
    return Object.values(this.dimensionTypesMapper).map((v) => this.t(v));
  }

  /**
   * MetricTypesを表示用の値にする
   * @returns - DimensionTypesを表示用の値にして返す
   */
  public getMetricTypesDisplayValue() {
    return Object.values(this.metricTypesMapper).map((v) => this.t(v));
  }

  /**
   * 選択肢のstringをDimensionTypeに変換して返す
   * @param value - DimensionType
   */
  public convertDisplayValueToDimensionType(value: string): DimensionType {
    const reverseMap = Object.fromEntries(Object.entries(this.dimensionTypesMapper).map(([k, v]) => [this.t(v), k]));
    const convertedValue = reverseMap[value] ?? '';
    return DashboardService.isDimensionType(convertedValue) ? convertedValue : DimensionTypes.UNKNOWN;
  }

  /**
   * 選択肢のstringをMetricTypeに変換して返す
   * @param value - DimensionType
   */
  public convertDisplayValueToMetricType(value: string): MetricType {
    const reverseMap = Object.fromEntries(Object.entries(this.metricTypesMapper).map(([k, v]) => [this.t(v), k]));
    const convertedValue = reverseMap[value] ?? '';
    return DashboardService.isMetricType(convertedValue) ? convertedValue : MetricTypes.UNKNOWN;
  }

  /**
   * メインのカスタム画面がデフォルト表示かを判断する
   * @param information - ダッシュボードの情報
   * @returns - メインのカスタム画面がデフォルト表示かどうか
   */
  public isDisplayDefaultView(information: DashboardInformation) {
    return DashboardService.isUnknown(information.dimensionType) || DashboardService.isUnknown(information.metricType);
  }

  /**
   * ヘッダーキー(IBusinessなどのkey)を表示用の値に変換する
   * @param key - キー
   */
  public convertHeaderKeyToDisplayValue(key: string): string {
    const i18nKey = `dashboard:${key}`;
    return this.t(i18nKey);
  }

  /**
   * ユーザー情報を表示用の情報に変換する
   * @param users - ユーザー情報(複数)
   */
  public convertUserAccountToDisplayValue(users: UserAccount[] | undefined): string {
    if (!users || users.length === 0) return '--';
    return users.map((u) => UserAccountService.getDisplayName(u)).join(',');
  }

  /**
   * 文字列情報を表示用の情報に変換する
   * @param key - ヘッダーキー
   * @param value - 実際の値
   */
  public convertNumberStringToDisplayValue(key: string, value: any): string {
    if (typeof value !== 'string' && typeof value !== 'number') return '--';
    if (typeof value === 'string') return Number(value).addComma();
    if (key === 'turnover') return Number(value).toFixed(2);
    if (key === 'forecastAccuracy') return (Number(value) * 100).toFixed(1) + '%';
    return Number(value.toFixed(2)).addComma();
  }

  /**
   * 値を表示用に変換する
   * @param key - 表示する値のキー
   * @param value - 変換する値
   */
  public convertNumberToDisplayValue(key: string, value?: number) {
    if (value === undefined) return '--';
    if (key === 'warningItemsCount' || key === 'criticalItemsCount') return value;
    if (key === 'averageForecastAccuracy') return (value * 100).toFixed(2);
    return value.toLocaleString(i18n.locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  /**
   * 表示用の値をnumberに変換する
   * @param value - 変換する値
   */
  public convertDisplayForecastAccuracyPeriodToNumber(value: string) {
    if (value === this.t('前月の正答率')) return 1;
    if (value === this.t('直近3ヶ月平均')) return 3;
    if (value === this.t('直近6ヶ月平均')) return 6;
    if (value === this.t('直近12ヶ月平均')) return 12;
    return undefined;
  }

  /**
   * numberを表示用の値に変換する
   * @param value - 変換する値
   */
  public convertNumberToDisplayForecastAccuracyPeriod(value: number | undefined) {
    if (value === 1) return this.t('前月の正答率');
    if (value === 3) return this.t('直近3ヶ月平均');
    if (value === 6) return this.t('直近6ヶ月平均');
    if (value === 12) return this.t('直近12ヶ月平均');
    return '';
  }
}
